import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import OrganisationSelection from '../components/onboarding/organisationSelection';
import AccessForbidden from '../components/accessForbidden/accessForbidden';
import { OutlookApp } from '../components/OutlookApp';
import Dashboard from '../components/dashboard/dashboard';
import MainLayout from '../components/layout/mainLayout';
import NoInternet from '../components/noInternet/noInternet';
import { LOCAL_STORAGE_NAMES, ROUTES } from '../constants/constants';
import ErrorPage from '../components/error/error';
import { useTranslation } from 'react-i18next';
import Analytics from '../components/analytics/analytics';
import { useEffect, useMemo, useState } from 'react';
import TransactionView from '../components/dashboard/transactionView/transactionView';
import NotesCard from '../components/dashboard/transactionView/notesCard';
import AddTransaction from '../components/dashboard/addTransaction/addTransaction';
import SelectMatter from '../components/dashboard/addTransaction/selectMatter';
import TransactionCardList from '../components/dashboard/addTransaction/transactionCardList';
import AddSkill from '../components/dashboard/addTransaction/addSkill';
import SubjectSuggestion from '../components/dashboard/addTransaction/subjectSuggestion';
import TaskLayout from '../components/task/taskLayout';
import TransactionInitializer from '../components/dashboard/addTransaction/transactionInitializer';
import TaskDetailView from '../components/task/taskDetailView';
import WorkTimeDetail from '../components/task/workTimeDetail';
import AddTask from '../components/task/addTask';
import TaskStatuses from '../components/task/taskStatuses';
import PriorityListing from '../components/task/priorityListing';
import CriticalTabView from '../components/criticalTask/criticalTabView';
import AddCriticalTask from '../components/criticalTask/addCriticalTask';
import PersonsListing from '../components/criticalTask/personsListing';
import MatterPhase from '../components/dashboard/addTransaction/matterPhase';
import TimerCardListing from '../components/timer/timerCardListing';
import AddTimer from '../components/timer/addTimer';
import TimerCardDetail from '../components/timer/timerCardDetail';
import { ITimer } from '../interface/timer';
import FloatingTimer from '../components/timer/floatingTimer';
import EditNote from '../components/dashboard/transactionView/editNote';
import ConflictSearchListing from '../components/conflictSearch/conflictSearchListing';
import ConflictDetailTab from '../components/conflictSearch/DetailView';
import CreateConflictCheck from '../components/conflictSearch/createConflictCheck';
import SelectUsers from '../components/conflictSearch/searchUsers';
import CreateMatterRequest from '../components/conflictSearch/createMatterRequest';
import TransactionTabView from '../components/transactions/transactionTabView';
import TimesheetTransactionList from '../components/transactions/timesheetTransactionList';
import Profile from '../components/profile/profile';
import ProfileAbout from '../components/profile/profileAbout';
import LanguageAndRegion from '../components/profile/languageAndRegion';
import Preferences from '../components/preferences/preferences';
import FeatureSelection from '../components/onboarding/featureSelection';
import CustomersListing from '../components/customers/customersListing';
import CustomerDetailLayout from '../components/customers/customerDetailLayout';
import ConflictCheckDetail from '../components/conflictSearch/conflictCheckDetail';
import MatterDetailView from '../components/matterDetail/matterDetailView';
import MatterTabView from '../components/matters/matterTabView';
import MatterRequestDetailView from '../components/matterRequestDetail/matterRequestDetailView';
import ExpenseDetailTabs from '../components/matterDetail/expense/expense-detail-tabs';
import InvoicingTabView from '../components/invoicing/invoicingTabView';
import BillableMatterDetailTabView from '../components/invoicing/billableDetailTabView';
import PreliminaryInvoiceTabView from '../components/invoicing/preliminaryInvoiceTabView';
import WorkTimeTabView from '../components/worktime/workTimeTabView';
import AddEditWorktime from '../components/worktime/addWorktime';
import WorkTimeTypes from '../components/worktime/workTimeTypes';
import ActivityListing from '../components/worktime/activityListing';
import { useQuickAction } from '../hooks/useQuickAction';
import QuickAction from '../components/quickAction/quickAction';
import TransactionMailIndicator from '../components/transactionMail/transactionMailIndicator';
import ManageMatterRequest from '../components/matterRequests/manageMatterRequest';
import SelectMatterRequestType from '../components/matterRequests/selectMatterRequestType';
import SelectLanguage from '../components/matterRequests/selectLanguage';
import SelectCurrency from '../components/matterRequests/selectCurrency';
import SelectHomeCountry from '../components/matterRequests/selectHomeCountry';
import SelectBusinessArea from '../components/matterRequests/selectBusinessArea';
import SelectMatterRequestUser from '../components/matterRequests/selectMatterRequestUser';
import { useFeatureContext } from '../context/FeatureContext';
import { useUser } from '../context/UserContext';
import { getStartModuleRoute } from '../utils/startModule';

const OutlookRoutes = () => {
  const { t, i18n } = useTranslation();
  const [isAnyTimerRunning, setIsAnyTimerRunning] = useState(false);
  const { shouldShowQuickAction, shouldShowTransactionMailAction } = useQuickAction();
  const { startModule } = useFeatureContext();
  const { user } = useUser();
  const routeTitles = useMemo(
    () => ({
      [ROUTES.DEFAULT]: t('common:organizationSelection'),
      [ROUTES.DASHBOARD]: t('common:mainHeader.dashboard'),
      [ROUTES.ACCESS_FORBIDDEN]: t('common:accessForbidden'),
      [ROUTES.NO_INTERNET]: t('common:noInternet'),
      [ROUTES.ANALYTICS]: t('common:appAnalytics'),
      [ROUTES.FEATURE_SELECTION]: t('common:features.setUpYourWorkspace'),
      [ROUTES.ERROR]: 'Error',
    }),
    [i18n.language],
  );
  const excludedRoutesForTimer = new Set([
    ROUTES.TIMER_LIST,
    ROUTES.DEFAULT,
    ROUTES.ADD_TRANSACTION_FROM_MAIL,
    ROUTES.ACCESS_FORBIDDEN,
    ROUTES.NO_INTERNET,
    ROUTES.ERROR,
  ]);
  const location = useLocation();
  const currentTitle = routeTitles[location.pathname] || '';

  const organisationUrl = (() => {
    const data = localStorage.getItem('currentOrganisation');
    return data ? JSON.parse(data)?.organizationApiUrl : null;
  })();

  const checkTimers = () => {
    const stored = localStorage.getItem(LOCAL_STORAGE_NAMES.timer);
    const user = JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAMES.user) ?? 'null');
    if (!stored) return setIsAnyTimerRunning(false);
    const timers = JSON.parse(stored);
    const isRunning = timers
      ?.filter((val: ITimer) => val.userId === user?.id)
      .some((t: ITimer) => t.isRunning);
    setIsAnyTimerRunning(isRunning && !excludedRoutesForTimer.has(location.pathname));
  };
  useEffect(() => {
    checkTimers();
    const onStorageChange = (e: StorageEvent) => {
      if (e.key === LOCAL_STORAGE_NAMES.timer) {
        checkTimers();
      }
    };

    globalThis.addEventListener('storage', onStorageChange);
    return () => globalThis.removeEventListener('storage', onStorageChange);
  }, [location?.pathname]);

  const getRedirectElement = () => {
    const onboardingFlow = sessionStorage.getItem(LOCAL_STORAGE_NAMES.isOnboarding);
    if (onboardingFlow) {
      return <Navigate to={ROUTES.FEATURE_SELECTION} replace />;
    }
    const isSelectingOrganisation = sessionStorage.getItem(
      LOCAL_STORAGE_NAMES.isSelectingOrganisation,
    );
    // A login stores the organisation before its user loads, and its own navigation decides where it lands.
    if (!organisationUrl || !user || isSelectingOrganisation) return <OrganisationSelection />;
    const item = Office.context.mailbox.item;
    if (!item) return <Navigate to={getStartModuleRoute(startModule)} replace />;
    return <Navigate to={ROUTES.ADD_TRANSACTION_FROM_MAIL} replace />;
  };
  return (
    <MainLayout title={currentTitle}>
      {isAnyTimerRunning && <FloatingTimer />}
      {shouldShowQuickAction && <QuickAction />}
      {shouldShowTransactionMailAction && <TransactionMailIndicator />}
      <Routes>
        <Route path={ROUTES.DEFAULT} key="organisation-selection" element={getRedirectElement()} />
        <Route path={ROUTES.DASHBOARD} key="dashboard" element={<Dashboard />} />
        <Route path={ROUTES.OUTLOOK_APP} key="outlook-app" element={<OutlookApp />} />
        <Route
          key="access-forbidden"
          path={ROUTES.ACCESS_FORBIDDEN}
          element={<AccessForbidden />}
        />
        <Route key="no-internet" path={ROUTES.NO_INTERNET} element={<NoInternet />} />

        <Route key="analytics" path={ROUTES.ANALYTICS} element={<Analytics />} />
        <Route
          key="transaction-view"
          path={ROUTES.TRANSACTION_VIEW + '/:id'}
          element={<TransactionView />}
        />
        <Route
          key="notes-view"
          path={ROUTES.NOTES_VIEW + '/:notesId'}
          element={<NotesCard detailedView />}
        />
        <Route
          key="edit-transaction"
          path={ROUTES.ADD_EDIT_TRANSACTION + '/:transactionId'}
          element={<AddTransaction />}
        />
        <Route
          key="add-transaction"
          path={ROUTES.ADD_EDIT_TRANSACTION}
          element={<AddTransaction />}
        />
        <Route
          key="transaction-mail"
          path={ROUTES.ADD_TRANSACTION_FROM_MAIL}
          element={<TransactionInitializer />}
        />
        <Route key="matter-selection" path={ROUTES.MATTER_SELECTION} element={<SelectMatter />} />
        <Route
          key="task-matter-selection"
          path={ROUTES.TASK_MATTER_SELECTION}
          element={<SelectMatter />}
        />
        <Route
          key="transaction-type-selection"
          path={ROUTES.TRANSACTION_TYPE_SELECTION}
          element={<TransactionCardList />}
        />
        <Route
          key="priority-selection"
          path={ROUTES.TASK_PRIORITY_SELECTION}
          element={<PriorityListing />}
        />
        <Route key="add-skills" path={ROUTES.ADD_SKILLS} element={<AddSkill />} />
        <Route key="add-matter-phase" path={ROUTES.ADD_MATTER_PHASE} element={<MatterPhase />} />
        <Route
          key="need-suggestions"
          path={ROUTES.NEED_SUGGESTIONS}
          element={<SubjectSuggestion />}
        />
        <Route key="tasks-list" path={ROUTES.TASKS_LIST} element={<TaskLayout />} />
        <Route
          key="tasks-detail"
          path={ROUTES.TASKS_DETAIL + '/:id'}
          element={<TaskDetailView />}
        />
        <Route
          key="tasks-detail"
          path={ROUTES.CRITICAL_TASKS_DETAIL + '/:id'}
          element={<CriticalTabView />}
        />
        <Route
          key="work-time-detail"
          path={ROUTES.WORK_TIME_DETAIL + '/:id'}
          element={<WorkTimeDetail />}
        />
        <Route key="add-task" path={ROUTES.ADD_EDIT_TASK} element={<AddTask />} />
        <Route key="edit-task" path={ROUTES.ADD_EDIT_TASK + '/:id'} element={<AddTask />} />
        <Route
          key="add-critical-task"
          path={ROUTES.ADD_EDIT_CRITICAL_TASK}
          element={<AddCriticalTask />}
        />
        <Route
          key="edit-critical-task"
          path={ROUTES.ADD_EDIT_CRITICAL_TASK + '/:id'}
          element={<AddCriticalTask />}
        />
        <Route key="person-listing" path={ROUTES.PERSON_LISTING} element={<PersonsListing />} />
        <Route key="task-statuses" path={ROUTES.TASK_STATUSES} element={<TaskStatuses />} />
        <Route key="timer-listing" path={ROUTES.TIMER_LIST} element={<TimerCardListing />} />
        <Route key="timer-add" path={ROUTES.TIMER_ADD_EDIT} element={<AddTimer />} />
        <Route key="timer-edit" path={ROUTES.TIMER_ADD_EDIT + '/:timerId'} element={<AddTimer />} />
        <Route
          key="timer-detail"
          path={ROUTES.TIMER_DETAIL + '/:timerId'}
          element={<TimerCardDetail />}
        />
        <Route key="notes-edit" path={ROUTES.ADD_EDIT_NOTES + '/:noteId'} element={<EditNote />} />
        <Route key="notes-add" path={ROUTES.ADD_EDIT_NOTES} element={<EditNote />} />
        <Route
          key="conflict-search"
          path={ROUTES.CONFLICT_SEARCH}
          element={<ConflictSearchListing />}
        />
        <Route
          key="conflict-search-detail"
          path={ROUTES.CONFLICT_SEARCH + '/:id'}
          element={<ConflictDetailTab />}
        />
        <Route
          key="create-conflict-check"
          path={ROUTES.CONFLICT_CHECKS}
          element={<CreateConflictCheck />}
        />
        <Route
          key="conflict-check-detail"
          path={ROUTES.CONFLICT_CHECKS + '/:id'}
          element={<ConflictCheckDetail />}
        />
        <Route
          key="create-matter-request"
          path={ROUTES.MATTER_REQUEST}
          element={<CreateMatterRequest />}
        />
        <Route key="select-user" path={ROUTES.SELECT_USER} element={<SelectUsers />} />
        <Route
          key="transaction-timesheet-listing"
          path={ROUTES.TRANSACTION_LISTING}
          element={<TransactionTabView />}
        />
        <Route
          key="transaction-timesheet-detail-listing"
          path={ROUTES.TRANSACTION_TIMESHEET_LIST}
          element={<TimesheetTransactionList />}
        />
        <Route key="profile" path={ROUTES.PROFILE} element={<Profile />} />
        <Route key="profile-about" path={ROUTES.PROFILE_ABOUT} element={<ProfileAbout />} />
        <Route
          key="language-and-region"
          path={ROUTES.LANGUAGE_AND_REGION}
          element={<LanguageAndRegion />}
        />
        <Route key="preferences" path={ROUTES.PREFERENCES} element={<Preferences />} />
        <Route
          key="feature-selection"
          path={ROUTES.FEATURE_SELECTION}
          element={<FeatureSelection />}
        />
        <Route
          key="customer-listing"
          path={ROUTES.CUSTOMER_LISTING}
          element={<CustomersListing />}
        />
        <Route
          key="customer-detail"
          path={ROUTES.CUSTOMER_LISTING + '/:id'}
          element={<CustomerDetailLayout />}
        />
        <Route
          key="customer-detail"
          path={ROUTES.CUSTOMER_LISTING + '/:id'}
          element={<CustomerDetailLayout />}
        />
        <Route
          key="matter-detail"
          path={ROUTES.MATTER_DETAIL + '/:id'}
          element={<MatterDetailView />}
        />
        <Route key="matter-tab" path={ROUTES.MATTERS_LISTING} element={<MatterTabView />} />
        <Route
          key="matter-tab"
          path={ROUTES.MATTER_REQUEST_DETAIL + '/:id'}
          element={<MatterRequestDetailView />}
        />
        <Route
          key="expense-detail-tab"
          path={ROUTES.EXPENSE_DETAIL + '/:id'}
          element={<ExpenseDetailTabs />}
        />
        <Route key="invoicing-tab" path={ROUTES.INVOICING} element={<InvoicingTabView />} />
        <Route
          key="billable-matter-tab"
          path={ROUTES.BILLABLE_MATTER_DETAIL + '/:id'}
          element={<BillableMatterDetailTabView />}
        />
        <Route
          key="preliminary-invoice-tab-view"
          path={ROUTES.PRELIMINARY_INVOICE_DETAIL + '/:id'}
          element={<PreliminaryInvoiceTabView />}
        />
        <Route key="worktime-tab" path={ROUTES.WORK_TIME} element={<WorkTimeTabView />} />
        <Route key="add-worktime" path={ROUTES.ADD_EDIT_WORK_TIME} element={<AddEditWorktime />} />
        <Route key="worktime-type" path={ROUTES.WORK_TIME_TYPE} element={<WorkTimeTypes />} />
        <Route
          key="edit-matter-request"
          path={ROUTES.MANAGE_MATTER_REQUEST + '/:id'}
          element={<ManageMatterRequest />}
        />
        <Route
          key="create-matter-request"
          path={ROUTES.MANAGE_MATTER_REQUEST}
          element={<ManageMatterRequest />}
        />
        <Route
          key="select-matter-request-type"
          path={ROUTES.MATTER_REQUEST_TYPE_LISTING}
          element={<SelectMatterRequestType />}
        />
        <Route key="select-language" path={ROUTES.LANGUAGE_LISTING} element={<SelectLanguage />} />
        <Route key="select-currency" path={ROUTES.CURRENCY_LISTING} element={<SelectCurrency />} />
        <Route
          key="select-home-country"
          path={ROUTES.COUNTRY_LISTING}
          element={<SelectHomeCountry />}
        />
        <Route
          key="select-business-area"
          path={ROUTES.BUSINESS_AREA_LISTING}
          element={<SelectBusinessArea />}
        />
        <Route
          key="select-matter-request-user"
          path={ROUTES.MATTER_REQUEST_USER_LISTING}
          element={<SelectMatterRequestUser />}
        />
        <Route
          key="edit-worktime"
          path={ROUTES.ADD_EDIT_WORK_TIME + '/:id'}
          element={<AddEditWorktime />}
        />
        <Route
          key="activity-selection"
          path={ROUTES.ACTIVITY_SELECTION}
          element={<ActivityListing />}
        />
        <Route key="error" path={ROUTES.ERROR} element={<ErrorPage />} />
        <Route path="*" key="page-not-found" element={<div>Page Not Found</div>} />
      </Routes>
    </MainLayout>
  );
};

export default OutlookRoutes;
